const LearningObjectTypes = {
  Course: 'course',
  LearningActivity: 'learningActivity',
  PlenaryLesson: 'plenaryLesson',
  PBL: 'pbl'
} as const;

export type LearningObjectTypes = (typeof LearningObjectTypes)[keyof typeof LearningObjectTypes];

const ElementType = {
  // 720 exam "Check Me" (PBI 537642): answers graded and locked, grade kept as `{ grade }`; Finish is ActivityFinished.
  ActivityChecked: 'activityChecked',
  ActivityFinished: 'activityFinished',
  AssignmentFeedback: 'assignmentFeedback',
  // The learner's last-viewed page, persisted so an external (720) launch can resume where they left
  // off. The player's page lives only in the URL, and a 720 iframe is re-created from its original
  // launch src on reload, so the URL is the one thing a refresh cannot carry.
  // Holds the page's CONTENT id, not the ordinal — `pageId` means the ordinal everywhere else in the
  // reporting vocabulary (see useEventData), and only the content id survives a re-ordering.
  CurrentPageContentId: 'currentPageContentId',
  DraftNoteToTeacher: 'draftNoteToTeacher',
  // Sequential-navigation progress of a 720 learning activity: the furthest unlocked page (a content id,
  // like CurrentPageContentId) and whether the final page was reached. See useSequentialNavigation.
  NavigationProgress: 'navigationProgress',
  // Pages of a 720 learning activity whose `completed` was already sent, keyed by page CONTENT id. It
  // records the REPORT, not the completion — that stays derived from the answers — so a page completed and
  // then closed before the learner left it is still reported on a later visit. See useCompletionEvents.
  AnnouncedPages: 'announcedPages',
  DraftFeedbackForAssignment: 'draftFeedbackForAssignment',
  SatisfactionScore: 'satisfactionScore',
  SubmissionHistory: 'submissionHistory',
  CollaborativeState: 'collaborativeState',
  SummaryPageSeen: 'summaryPageSeen',
  PreviousQuestionsAnswers: 'previousQuestionsAnswers',
  ManualFeedback: 'manualFeedback',
  QuestionGroups: 'questionGroups',
  Question: 'question',
  Status: 'status',
  ReturnToFix: 'returnToFix'
} as const;
export type ElementType = (typeof ElementType)[keyof typeof ElementType];

const FEEDBACK_ELEMENT_ID_SUFFIX = '_feedback';

function getFeedbackElementId(questionId: string): string {
  return `${questionId}${FEEDBACK_ELEMENT_ID_SUFFIX}`;
}

function getQuestionIdFromFeedbackElementId(feedbackId: string): string {
  return feedbackId.replace(new RegExp(`${FEEDBACK_ELEMENT_ID_SUFFIX}$`), '');
}

function isFeedbackElementId(id: string): boolean {
  return id.endsWith(FEEDBACK_ELEMENT_ID_SUFFIX);
}

const FeedbackMessages = {
  NoAnswer: 'noAnswer',
  OnlyWrongAnswers: 'onlyWrongAnswers',
  NotAllCorrectAndNoWrongAnswers: 'notAllCorrectAndNoWrongAnswers',
  SomeCorrectAndSomeWrongAnswers: 'someCorrectAndSomeWrongAnswers',
  OnlyAllCorrectAnswers: 'onlyAllCorrectAnswers',
  ShowAnswer: 'showAnswer',
  NoAnswerButNotRequired: 'noAnswerButNotRequired'
} as const;
export type FeedbackMessages = (typeof FeedbackMessages)[keyof typeof FeedbackMessages];

const CourseEntryMessageType = {
  NoEnvironments: 'noEnvironments',
  NoClassesAndGroups: 'noClassesAndGroups'
} as const;
export type CourseEntryMessageType = (typeof CourseEntryMessageType)[keyof typeof CourseEntryMessageType];

const StateStatus = {
  Unsubmitted: 'Unsubmitted',
  Submitted: 'Submitted',
  ReturnedToFix: 'ReturnedToFix',
  Evaluated: 'Evaluated'
} as const;
export type StateStatus = (typeof StateStatus)[keyof typeof StateStatus];

const TaskStatusEnum = {
  ToDo: 'todo',
  Done: 'done',
  Late: 'late',
  ReturnedForCorrection: 'returnedForCorrection',
  TillTomorrow: 'tillTomorrow',
  Checked: 'checked'
} as const;
export type TaskStatusEnum = (typeof TaskStatusEnum)[keyof typeof TaskStatusEnum];

const DashboardTaskType = {
  SelfLearning: 'selfLearning',
  LearningWork: 'learningWork',
  EvalWork: 'evalWork',
  Exam: 'exam'
} as const;
export type DashboardTaskType = (typeof DashboardTaskType)[keyof typeof DashboardTaskType];

const TaskStatus = {
  Started: 'started',
  Submitted: 'submitted',
  Assessed: 'assessed'
} as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

const PlenaryStatus = {
  Started: 'Started',
  Stopped: 'Stopped',
  New: 'New'
} as const;
export type PlenaryStatus = (typeof PlenaryStatus)[keyof typeof PlenaryStatus];

const AssignmentType = {
  nonGraded: 'nonGraded',
  graded: 'graded',
  exam: 'exam',
  selfLearning: 'selfLearning'
} as const;
export type AssignmentType = (typeof AssignmentType)[keyof typeof AssignmentType];

const AssignmentFeedbackTrigger = {
  studentSubmit: 'student-submit',
  teacherSubmit: 'teacher-submit',
  date: 'date'
} as const;
export type AssignmentFeedbackTrigger = (typeof AssignmentFeedbackTrigger)[keyof typeof AssignmentFeedbackTrigger];

export {
  AssignmentFeedbackTrigger,
  AssignmentType,
  CourseEntryMessageType,
  DashboardTaskType,
  ElementType,
  FEEDBACK_ELEMENT_ID_SUFFIX,
  FeedbackMessages,
  getFeedbackElementId,
  getQuestionIdFromFeedbackElementId,
  isFeedbackElementId,
  LearningObjectTypes,
  PlenaryStatus,
  StateStatus,
  TaskStatus,
  TaskStatusEnum
};
